import type { InternalAxiosRequestConfig } from 'axios';
import type { AuthStoreAdapter } from '../types/auth';
import type { ClientState } from '../types/state';
import type { TokenRefreshConfig } from '../types/config';
import { isTokenNearExpiry } from '../token/expiry';

/**
 * Dependencies for request interceptor.
 */
export interface RequestInterceptorDependencies {
  /** Auth store for getting tokens */
  authStore: AuthStoreAdapter;

  /** Client state */
  state: ClientState;

  /** Token refresh configuration */
  config: TokenRefreshConfig;

  /** Function to handle token refresh */
  handleTokenRefresh: () => Promise<string>;

  /** Client type for version header */
  clientType: 'portal' | 'mobile';

  /** App version for version header */
  appVersion: string;

  /** Optional callback to get current network connection type */
  getConnectionType?: () => string;
}

/**
 * Request interceptor function.
 *
 * Responsibilities:
 * 1. Add Authorization header with current token
 * 2. Check if token is near expiry
 * 3. Proactively refresh token if needed (before making request)
 * 4. Continue with new token if refresh succeeds
 * 5. Continue with existing token if refresh fails (response interceptor will handle it)
 *
 * This interceptor prevents making requests with tokens that are about to expire,
 * reducing the likelihood of 401 errors.
 *
 * @param deps Dependencies (authStore, state, config, handlers)
 * @returns Request interceptor function for axios
 */
export function createRequestInterceptor(deps: RequestInterceptorDependencies) {
  const { authStore, state, config, handleTokenRefresh, clientType, appVersion, getConnectionType } = deps;

  return async (
    requestConfig: InternalAxiosRequestConfig
  ): Promise<InternalAxiosRequestConfig> => {
    // Ensure headers object exists
    requestConfig.headers = requestConfig.headers || {};

    // Add version headers to all requests
    requestConfig.headers['X-Client-Version'] = appVersion;
    requestConfig.headers['X-Client-Type'] = clientType;

    // Add connection type header if available (native mobile apps only)
    if (getConnectionType) {
      const connectionType = getConnectionType();
      if (connectionType) {
        requestConfig.headers['X-Client-Connection-Type'] = connectionType;
      }
    }

    const token = authStore.getToken();

    if (token) {
      // Check if token needs refresh proactively
      if (isTokenNearExpiry(token, config)) {
        const refreshToken = authStore.getRefreshToken();

        if (refreshToken && !state.refreshPromise) {
          try {
            // Start proactive refresh in background
            const newToken = await handleTokenRefresh();
            requestConfig.headers.Authorization = `Bearer ${newToken}`;
          } catch (error) {
            // If proactive refresh fails, continue with existing token
            // The response interceptor will handle it if it's truly expired
            console.warn(
              "Proactive token refresh failed, continuing with existing token:",
              error
            );
            requestConfig.headers.Authorization = `Bearer ${token}`;
          }
        } else {
          requestConfig.headers.Authorization = `Bearer ${token}`;
        }
      } else {
        requestConfig.headers.Authorization = `Bearer ${token}`;
      }
    }

    return requestConfig;
  };
}

/**
 * Request interceptor error handler.
 * Simply rejects the promise with the error.
 */
export function requestInterceptorErrorHandler(error: unknown): Promise<never> {
  return Promise.reject(error);
}
